import React, { useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Container,
  Button,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Chip,
  Alert,
  AlertTitle,
  Tooltip,
  Avatar,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Email as EmailIcon,
  PhoneAndroid as AuthenticatorIcon,
  ArrowBack as BackIcon,
  Home as HomeIcon,
  Shield as ShieldIcon,
} from '@mui/icons-material';
import { CompanyOption, PendingCounts } from 'shared/services/authService';
import { getRoleDisplayName } from 'shared/utils/roleUtils';
import { UserRole } from 'shared/types';

interface CompanySelectorProps {
  email: string;
  companies: CompanyOption[];
  onSelect: (companyId: string) => void;
  onBack: () => void;
  isLoading?: boolean;
}

export const CompanySelector: React.FC<CompanySelectorProps> = ({
  email,
  companies,
  onSelect,
  onBack,
  isLoading = false,
}) => {
  // External (borrowed) entries first, then the user's own company, then
  // anything else — alphabetically within each group. Reversed from the
  // original "home first" order per client feedback: a provider admin
  // lent to a client almost always wants to land in the client, not their
  // own company's admin view, so that access should be the obvious pick.
  const sortedCompanies = useMemo(() => {
    const rank = (c: CompanyOption) => (c.isExternal ? 0 : c.isHome ? 1 : 2);
    return [...companies].sort((a, b) => {
      const r = rank(a) - rank(b);
      if (r !== 0) return r;
      return a.name.localeCompare(b.name, 'es');
    });
  }, [companies]);

  /**
   * Build a pluralised, formal-Spanish breakdown of pending items for the
   * tooltip. Empty buckets are omitted so the tooltip stays tight.
   */
  const buildPendingBreakdown = (p: PendingCounts): string => {
    const parts: string[] = [];
    if (p.verifications > 0) {
      parts.push(
        `${p.verifications} ${p.verifications === 1 ? 'verificación' : 'verificaciones'}`,
      );
    }
    if (p.evaluations > 0) {
      parts.push(
        `${p.evaluations} ${p.evaluations === 1 ? 'evaluación' : 'evaluaciones'}`,
      );
    }
    if (p.permits > 0) {
      parts.push(
        `${p.permits} ${p.permits === 1 ? 'permiso' : 'permisos'}`,
      );
    }
    if (p.serviceCards > 0) {
      parts.push(
        `${p.serviceCards} ${p.serviceCards === 1 ? 'ficha' : 'fichas'}`,
      );
    }
    return parts.join(', ');
  };

  /**
   * Solid pill in the warning tone with a white number. Hover/tap shows
   * the breakdown so the row stays uncluttered.
   */
  const renderPendingBadge = (pending: PendingCounts | null | undefined) => {
    if (!pending || pending.total <= 0) return null;
    const breakdown = buildPendingBreakdown(pending);
    return (
      <Tooltip
        title={
          breakdown
            ? `Pendientes en esta empresa: ${breakdown}.`
            : 'Hay elementos pendientes en esta empresa.'
        }
        arrow
      >
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: 28,
            height: 24,
            px: 1,
            borderRadius: 999,
            bgcolor: 'warning.main',
            color: '#fff',
            fontWeight: 700,
            fontSize: '0.78rem',
            lineHeight: 1,
            flexShrink: 0,
          }}
        >
          {pending.total}
        </Box>
      </Tooltip>
    );
  };

  const get2FAMethodLabel = (method: 'email' | 'authenticator' | null) => {
    if (!method) return null;
    const isAuth = method === 'authenticator';
    return (
      <Chip
        icon={isAuth ? <AuthenticatorIcon sx={{ fontSize: 14 }} /> : <EmailIcon sx={{ fontSize: 14 }} />}
        label={isAuth ? 'Autenticador' : 'Email'}
        size="small"
        sx={{
          height: 20,
          fontSize: '0.7rem',
          bgcolor: 'action.hover',
          color: 'text.secondary',
          border: 'none',
          '& .MuiChip-icon': { color: 'text.secondary' },
        }}
      />
    );
  };

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          minHeight: '100dvh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          py: 3,
        }}
      >
        <Card sx={{ width: '100%', maxWidth: 450 }}>
          <CardContent sx={{ p: 4 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  bgcolor: (theme) => theme.palette.background.primary,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2,
                }}
              >
                <BusinessIcon sx={{ fontSize: 32, color: 'white' }} />
              </Box>
              <Typography variant="h5" align="center" fontWeight="bold">
                ¿Con cuál empresa va a trabajar hoy?
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                Su cuenta tiene acceso a varias empresas. Elija una para empezar.
              </Typography>
            </Box>

            {/* Explainer — non-technical: why we ask, and that data stays
                separate per company. */}
            <Alert
              severity="info"
              icon={<ShieldIcon fontSize="small" />}
              sx={{ mb: 3 }}
            >
              <AlertTitle sx={{ fontWeight: 600, mb: 0.75 }}>
                Una empresa a la vez
              </AlertTitle>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Para mantener la información de cada empresa separada y privada,
                solo puede trabajar con una empresa por sesión.
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Cuando entra a una empresa, solo verá lo que pertenece a esa
                empresa: sus permisos de trabajo, fichas de servicio, evaluaciones,
                usuarios, mensajes, notificaciones, documentos y reportes.
                Nada se comparte entre empresas.
              </Typography>
              <Typography variant="body2">
                Si necesita cambiar, puede usar la opción "Cambiar de empresa" en
                el menú de su perfil, o cerrar sesión y volver a entrar.
              </Typography>
            </Alert>

            {/* Company List — home company first, marked "Mi empresa".
                Border-less rows; logo (or fallback icon) + name + role +
                a single warning-toned counter for pending items. */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 1 }}>
              {sortedCompanies.map((company) => {
                const planBlocked = company.hasOwnActivePlan === false;
                return (
                <ListItemButton
                  key={company.id}
                  onClick={() => onSelect(company.id)}
                  disabled={isLoading || planBlocked}
                  sx={{
                    borderRadius: 2,
                    py: 1.5,
                    px: 1.5,
                    ...(company.isHome && !planBlocked && {
                      bgcolor: (theme) => `${theme.palette.background.primary}0A`,
                    }),
                    ...(planBlocked && { opacity: 0.6 }),
                    '&:hover': {
                      bgcolor: (theme) => `${theme.palette.background.primary}14`,
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 52 }}>
                    {company.logoUrl ? (
                      <Avatar
                        src={company.logoUrl}
                        alt={company.name}
                        sx={{ width: 40, height: 40 }}
                        variant="rounded"
                      />
                    ) : (
                      <Avatar
                        sx={{
                          width: 40,
                          height: 40,
                          bgcolor: (theme) => `${theme.palette.background.primary}1A`,
                          color: (theme) => theme.palette.background.primary,
                        }}
                        variant="rounded"
                      >
                        {company.isHome ? <HomeIcon sx={{ fontSize: 22 }} /> : <BusinessIcon sx={{ fontSize: 22 }} />}
                      </Avatar>
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75 }}>
                        <Typography variant="subtitle1" fontWeight={600}>
                          {company.name}
                        </Typography>
                        {company.isHome && (
                          <Chip
                            label="Mi empresa"
                            size="small"
                            sx={{
                              height: 20,
                              fontWeight: 600,
                              fontSize: '0.7rem',
                              bgcolor: (theme) => `${theme.palette.background.primary}1A`,
                              color: (theme) => theme.palette.background.primary,
                              border: 'none',
                            }}
                          />
                        )}
                        {company.isExternal && (
                          <Tooltip title="Un cliente de su empresa le dio acceso para trabajar aquí" arrow>
                            <Chip
                              label="Empresa que atiendo"
                              size="small"
                              sx={{
                                height: 20,
                                fontWeight: 600,
                                fontSize: '0.7rem',
                                bgcolor: 'success.main',
                                color: 'success.contrastText',
                                border: 'none',
                              }}
                            />
                          </Tooltip>
                        )}
                        {planBlocked && (
                          <Tooltip title="Contacte a su Soporte Monitor para adquirir un plan" arrow>
                            <Chip
                              label="Sin plan activo"
                              size="small"
                              sx={{
                                height: 20,
                                fontWeight: 600,
                                fontSize: '0.7rem',
                                bgcolor: 'warning.main',
                                color: 'warning.contrastText',
                                border: 'none',
                              }}
                            />
                          </Tooltip>
                        )}
                        {get2FAMethodLabel(company.twoFactorMethod)}
                      </Box>
                    }
                    secondary={
                      <Typography variant="body2" color="text.secondary" component="span">
                        {planBlocked
                          ? 'Sin plan propio — no se puede ingresar a esta empresa'
                          : getRoleDisplayName(company.role as UserRole)}
                      </Typography>
                    }
                  />
                  {!planBlocked && renderPendingBadge(company.pending)}
                </ListItemButton>
                );
              })}
            </Box>

            {/* El bloqueo por falta de plan propio se veía solo como una fila
                gris con un tooltip: ni por qué, ni qué hacer al respecto.
                Un admin de proveedora sin plan propio SÍ puede seguir
                trabajando dentro de las empresas que atiende, así que lo
                importante es que no lea la fila gris como "mi cuenta está
                mal". */}
            {companies.some((c) => c.hasOwnActivePlan === false) && (
              <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
                <AlertTitle sx={{ fontSize: '0.95rem' }}>
                  ¿Por qué no puedo entrar a mi empresa?
                </AlertTitle>
                <Typography variant="body2">
                  Su empresa aparece en gris porque todavía no tiene un plan propio
                  activo. Mientras tanto puede entrar normalmente a las empresas que
                  atiende y trabajar ahí sus permisos, fichas y documentos.
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Para activar su propia empresa, contacte a su Soporte Monitor — sus
                  datos aparecen en el Inicio de cualquiera de las empresas que atiende.
                </Typography>
              </Alert>
            )}

            {/* Back Button */}
            <Box sx={{ mt: 3 }}>
              <Button
                fullWidth
                variant="outlined"
                onClick={onBack}
                disabled={isLoading}
                startIcon={<BackIcon />}
                sx={{
                  borderColor: (theme) => theme.palette.background.primary,
                  color: (theme) => theme.palette.background.primary,
                  '&:hover': {
                    borderColor: (theme) => theme.palette.background.primary,
                    bgcolor: (theme) => `${theme.palette.background.primary}10`,
                  },
                }}
              >
                Volver al inicio de sesión
              </Button>
            </Box>

            {/* Email hint */}
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', textAlign: 'center', mt: 2 }}
            >
              Iniciando sesión como {email}
            </Typography>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default CompanySelector;
